<template>
  <modal
    id="deal-end-modal"
    :is-opened="isOpened"
    :on-close="close"
    data-cy="adaf-deal-end-modal"
  >
    <template #header>
      {{ trans('organization.business_input.deal_row.end_modal.title') }}
    </template>
    <template #body>
      <div class="modal-state">
        <!-- eslint-disable vue/no-v-html -->
        <p v-html="trans('organization.business_input.deal_row.end_modal.text')" />
        <!-- eslint-enable vue/no-v-html -->
        <label>{{ trans('organization.business_input.deal_row.end_modal.end_date_label') }}</label>
        <div class="datepicker">
          <vue-date-picker
            v-model="vuelidateState.date.$model"
            format="dd/MM/yyyy"
            :min-date="disabledDates"
            :enable-time-picker="false"
            name="date_end_deal"
            locale="fr"
            :input-class="inputClass"
            auto-apply
          >
            <template #input-icon>
              <span class="datepicker__icon datepicker__icon--datepicker" />
            </template>
          </vue-date-picker>
        </div>
        <span
          v-if="vuelidateState.date.$error"
          class="input-text error-message"
        >
          {{ trans('organization.business_input.deal_row.end_modal.end_date_error') }}
        </span>
        <span v-if="errorMessage" class="input-text error-message">
          {{ trans(errorMessage) }}
        </span>
        <p v-if="mailto !== ''">
          {{ trans('organization.business_input.deal_row.end_modal.help') }}
          <a :href="mailto">{{ companyName }}</a>.
        </p>
        <div class="modal-state__actions">
          <button class="btn btn--secondary" @click="close()">
            {{ trans('global.cancel') }}
          </button>
          <button
            class="btn btn--primary modal-default-button confirm-button"
            :class="{ disabled: vuelidateState.$error, 'loading-btn': loading }"
            @click="ok"
          >
            <span>{{ trans('global.confirm') }}</span>
          </button>
        </div>
      </div>
    </template>
  </modal>
</template>

<script>
import { ref, computed } from 'vue';
import axios from 'axios';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import moment from 'moment';
import VueDatePicker from '@vuepic/vue-datepicker';
import Modal from '@components/Modal.vue';
import { flashBagError, flashBagSuccess } from '@tools/flashbag';
import { URL_ADAF_DEAL_END } from '@tools/routes/routes';
import { trans, translationMixin } from '@elements/translation';
import { eventBus } from '@tools/event-bus';
import { getNowToDisableDateForAdafModals } from './date-mixins';

export default {
  components: {
    Modal, VueDatePicker,
  },
  mixins: [ translationMixin ],
  props: {
    isOpened: { type: Boolean, default: false },
    dealId: { type: Number, default: 0 },
    endDate: { type: Date, default: null },
    accountManagerEmail: { type: String, default: '' },
    dealBuyerFullname: { type: String, default: '' },
  },
  setup(props, { emit }) {
    const date = ref(props.endDate || new Date());
    const loading = ref(false);
    const error = ref(false);
    const inputClass = ref(null);
    const errorMessage = ref(null);
    const companyName = computed(() => window.COMPANY_NAME);
    const disabledDates = getNowToDisableDateForAdafModals();

    const vuelidateState = useVuelidate({
      date: { required },
    }, { date });

    const mailto = computed(() => {
      if (!props.accountManagerEmail || !props.dealBuyerFullname) {
        return '';
      }
      return `mailto:${props.accountManagerEmail}?subject=${trans(
        'organization.business_input.deal_row.end_modal.prefill_subject',
        { fullname: props.dealBuyerFullname },
      )}`;
    });

    const clear = () => {
      date.value = new Date();
      error.value = false;
      loading.value = false;
      inputClass.value = null;
      errorMessage.value = null;
    };

    const close = () => {
      clear();
      emit('close');
      vuelidateState.value.$reset();
    };

    const ok = async () => {
      vuelidateState.value.$touch();
      if (vuelidateState.value.$error) {
        inputClass.value = 'error';
        return;
      }

      if (loading.value) return;
      loading.value = true;

      try {
        const response = await axios.post(URL_ADAF_DEAL_END.replace('{id}', props.dealId), {
          end_date: moment(date.value.toISOString()).format('YYYY-MM-DD'),
        });
        // Handle success response
        eventBus.dispatchEvent(new CustomEvent('deal-ended', {
          detail: {
            dealId: props.dealId,
            endDate: { unix: date.value.getTime() / 1000, iso: date.value.toISOString() },
            quantity: response.data.quantity,
          },
        }));

        flashBagSuccess(trans('organization.business_input.deal_row.end_modal.success_message'));
        close();
      } catch (err) {
        errorMessage.value = err.response?.data?.error || 'global.error';
        flashBagError(trans(errorMessage.value));
      } finally {
        loading.value = false;
      }
    };

    return {
      disabledDates,
      companyName,
      date,
      loading,
      error,
      inputClass,
      errorMessage,
      mailto,
      vuelidateState,
      clear,
      close,
      ok,
    };
  },
};
</script>
